"use client";

import type { ReactNode } from "react";
import { BaseModal } from "@/components/ui/base-modal";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type WizardStepConfig = {
  id: string;
  label: string;
};

type ExpressWizardShellProps = {
  open: boolean;
  title: string;
  description?: string;
  steps: WizardStepConfig[];
  currentStepId: string;
  children: ReactNode;
  onBack?: () => void;
  onNext?: () => void;
  onFinish?: () => void;
  nextLabel?: string;
  finishLabel?: string;
  isNextLoading?: boolean;
  isNextDisabled?: boolean;
  showBack?: boolean;
  isLastStep?: boolean;
  blockDismiss?: boolean;
  onOpenChange?: (open: boolean) => void;
  dialogContentClassName?: string;
  hideFooter?: boolean;
  hidePrimaryAction?: boolean;
  onContinueLater?: () => void;
};

export function ExpressWizardShell({
  open,
  title,
  description,
  steps,
  currentStepId,
  children,
  onBack,
  onNext,
  onFinish,
  nextLabel = "Continue",
  finishLabel = "Finish",
  isNextLoading = false,
  isNextDisabled = false,
  showBack = true,
  isLastStep = false,
  blockDismiss = true,
  onOpenChange,
  dialogContentClassName,
  hideFooter = false,
  hidePrimaryAction = false,
  onContinueLater,
}: ExpressWizardShellProps) {
  const currentIndex = steps.findIndex((step) => step.id === currentStepId);

  const footer = (
    <div className="flex w-full items-center justify-between gap-3">
      <div className="flex min-w-0 items-center gap-3">
        {showBack && onBack ? (
          <Button type="button" variant="outline" onClick={onBack}>
            Back
          </Button>
        ) : null}
      </div>
      <div className="flex items-center gap-3">
        {onContinueLater && !isLastStep ? (
          <button
            type="button"
            className="text-sm text-muted-foreground underline-offset-4 hover:underline"
            onClick={onContinueLater}
          >
            Continue later
          </button>
        ) : null}
        {!hidePrimaryAction && (
          isLastStep ? (
            <Button
              type="button"
              onClick={onFinish}
              disabled={isNextLoading || isNextDisabled}
            >
              {finishLabel}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={onNext}
              disabled={isNextLoading || isNextDisabled}
            >
              {nextLabel}
            </Button>
          )
        )}
      </div>
    </div>
  );

  return (
    <BaseModal
      open={open}
      onOpenChange={blockDismiss ? undefined : onOpenChange}
      hideCloseButton={blockDismiss}
      title={title}
      description={description}
      footer={hideFooter ? null : footer}
      dialogContentClassName={cn("max-w-2xl", dialogContentClassName)}
    >
      <div className="space-y-6 overflow-x-hidden">
        <div className="-mx-1 overflow-x-auto pb-1">
          <div className="flex w-max min-w-full items-center gap-3 px-1">
            {steps.map((step, index) => {
              const isActive = step.id === currentStepId;
              const isComplete = index < currentIndex;

              return (
                <div
                  key={step.id}
                  className="flex shrink-0 items-center gap-2"
                >
                  <div
                    className={cn(
                      "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-medium",
                      isActive && "bg-primary text-primary-foreground",
                      isComplete && "bg-primary/20 text-primary",
                      !isActive &&
                        !isComplete &&
                        "bg-muted text-muted-foreground"
                    )}
                  >
                    {index + 1}
                  </div>
                  <span
                    className={cn(
                      "whitespace-nowrap text-xs",
                      isActive
                        ? "font-medium text-foreground"
                        : "text-muted-foreground"
                    )}
                  >
                    {step.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="overflow-x-hidden">{children}</div>
      </div>
    </BaseModal>
  );
}
